import React from 'react';
import {
  PlainButton,
  PaddingWrapper,
  DrawerHeader,
} from 'components/styled-components';
import { Drawer } from '@blueprintjs/core';
import FormSelectGroup from 'components/forms/components/form-select-group';
import FormTextGroup from 'components/forms/components/form-text-group';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { setAllValues } from 'utils/functions';
import filter from 'lodash/filter';
import isEqual from 'lodash/isEqual';
import colors from 'utils/colors';

const AddUserSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(1, 'Too Short!')
    .max(200, 'Too Long!')
    .trim()
    .required('Required'),
  lastName: Yup.string()
    .min(1, 'Too Short!')
    .max(200, 'Too Long!')
    .trim()
    .required('Required'),
  email: Yup.string()
    .min(1, 'Too Short!')
    .max(200, 'Too Long!')
    .email('Invalid email')
    .required('Required'),
});

const EditUserSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(1, 'Too Short!')
    .max(150, 'Too Long!')
    .trim()
    .ensure(),
  lastName: Yup.string()
    .min(1, 'Too Short!')
    .max(200, 'Too Long!')
    .trim()
    .ensure(),
  email: Yup.string()
    .min(1, 'Too Short!')
    .max(255, 'Too Long!')
    .email('Invalid email'),
});

const UserDrawer = ({
  isOpen,
  handleCloseDrawer,
  handleSelectChange,
  handleTextChange,
  handleResendEmail,
  user,
  newUser,
  handleSaveUser,
  handleSaveAndAddAnotherUser,
  departments,
  roles,
  initialUser,
  vendors,
  status,
  initialUserDepartmentString,
}) => {
  const validateSelectValue = value => {
    let error;
    if ((value === null || value === undefined) && newUser) {
      error = 'Required';
    }
    return error;
  };

  const compareObjOrString = (value, comparator, key = 'value') => {
    if (typeof value === 'string') {
      return value.toLowerCase() === comparator;
    } else {
      return value[key].toLowerCase() === comparator;
    }
  };

  const resendEmail = (event, emailType) => {
	event.preventDefault();
	handleResendEmail(user.userID, emailType);
  }

  const validDepartment =
    departments.filter(
      department => department.departmentName === initialUserDepartmentString
    ).length || newUser;
  return (
    <Drawer isOpen={isOpen} onClose={handleCloseDrawer}>
      <Formik
        initialValues={user}
        validationSchema={newUser ? AddUserSchema : EditUserSchema}
        onSubmit={(values, actions) =>
          handleSaveUser(initialUser, values, actions)
        }
      >
        {({
          isSubmitting,
          setTouched,
          validateForm,
          errors,
          values,
          ...rest
        }) => (
          <Form>
            <DrawerHeader>
              <span>{newUser ? 'Add User' : 'Edit User'}</span>
              <div>
                {!isEqual(user, initialUser) && (
                  <PlainButton type="submit" disabled={isSubmitting}>
                    SAVE
                  </PlainButton>
                )}

                {newUser && (
                  <PlainButton
                    type="button"
                    onClick={() => {
                      setTouched(setAllValues(user, true));
                      validateForm().then(allErrors => {
                        if (Object.keys(allErrors).length === 0) {
                          rest.setSubmitting(true);
                          handleSaveAndAddAnotherUser(values, rest);
                        }
                      });
                    }}
                    disabled={isSubmitting}
                  >
                    SAVE & Add Another
                  </PlainButton>
                )}
              </div>
            </DrawerHeader>
            <PaddingWrapper padding="2rem">
              {!newUser && (
                <FormTextGroup
                  label="User ID"
                  name="userID"
                  value={user.userID}
                  disabled
                />
              )}

              <FormTextGroup
                label="First Name"
                requiredLabel={newUser}
                name="firstName"
                value={user.firstName}
                handleChange={handleTextChange}
              />
              <FormTextGroup
                label="Last Name"
                requiredLabel={newUser}
                name="lastName"
                value={user.lastName}
                handleChange={handleTextChange}
              />
              <FormTextGroup
                label="Email Address"
                requiredLabel={newUser}
                name="email"
                value={user.email}
                handleChange={handleTextChange}
              />
              {!newUser && (
                <FormSelectGroup
                  label="Status"
                  requiredLabel={newUser}
                  name="status"
                  asyncOptions={status}
                  labelKey="statusName"
                  valueKey="userStatusID"
                  value={user.status}
                  onChange={handleSelectChange}
                  isClearable={false}
                />
              )}
              {((user.role &&
                !compareObjOrString(user.role, 'third party', 'displayName')) ||
                newUser) && (
                <Field
                  name="role"
                  validate={validateSelectValue}
                  render={() => (
                    <FormSelectGroup
                      label="Role"
                      requiredLabel={newUser}
                      name="role"
                      asyncOptions={
                        newUser
                          ? roles
                          : filter(
                              roles,
                              role => role.roleName !== 'THIRD_PARTY'
                            )
                      }
                      labelKey="displayName"
                      valueKey="roleName"
                      value={user.role}
                      onChange={handleSelectChange}
                      isClearable={false}
                    />
                  )}
                />
              )}
              {user.role &&
                compareObjOrString(user.role, 'third party', 'displayName') && (
                  <Field
                    name="thirdParties"
                    validate={validateSelectValue}
                    render={() => (
                      <FormSelectGroup
                        label="Third Party Name"
                        requiredLabel={newUser}
                        name="thirdParties"
                        asyncOptions={filter(vendors, [
                          'vendorStatus',
                          'Active',
                        ])}
                        labelKey="vendorName"
                        valueKey="vendorID"
                        value={user.thirdParties}
                        onChange={handleSelectChange}
                        isClearable={false}
						multi
                      />
                    )}
                  />
                )}

              {user.role &&
                !compareObjOrString(
                  user.role,
                  'third party',
                  'displayName'
                ) && (
                  <>
                    <Field
                      name="department"
                      validate={validateSelectValue}
                      render={() => (
                        <FormSelectGroup
                          label=" Department"
                          requiredLabel={newUser}
                          name="department"
                          asyncOptions={departments}
                          labelKey="departmentName"
                          valueKey="departmentID"
                          value={
                            validDepartment || !!user.department
                              ? user.department
                              : {
                                  departmentID: initialUserDepartmentString,
                                  departmentName: initialUserDepartmentString,
                                }
                          }
                          onChange={handleSelectChange}
                          noOptionsMessage="No departments were found. Create a new one in the Admin Tools"
                          isClearable={false}
                        />
                      )}
                    />
                    {!validDepartment && (
                      <span style={{ color: colors.darkRed }}>
                        {initialUserDepartmentString === null
                          ? `Department is not set.  Please select a valid department from the dropdown above.`
                          : `Department: "${initialUserDepartmentString}" is no longer active. No action is required.`}
                      </span>
                    )}
                  </>
                )}
				{!newUser && (
                	<>
						<PlainButton disabled={isSubmitting} style={{marginLeft: "0", display: "block", marginBottom: "1rem"}} onClick={(e) => resendEmail(e, 'welcome')}>Resend Welcome Email</PlainButton>
						<PlainButton disabled={isSubmitting} style={{marginLeft: "0", display: "block", marginBottom: "1rem"}} onClick={(e) => resendEmail(e, 'password')}>Reset Password and send Email</PlainButton>
					</>
              	)}
            </PaddingWrapper>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default UserDrawer;
